import React, { useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const ProvisionPreviewDialog = ({
    visible,
    onHide,
    onConfirm,
    previewData,
    isLoading,
    isError,
    isApproveAll = false
}) => {
    const { th } = useLanguage();

    const groups = previewData?.groups || [];

    const grandTotals = useMemo(() => {
        return groups.reduce(
            (acc, g) => ({
                totalClaims: acc.totalClaims + g.claimCount,
                partsTotal: acc.partsTotal + g.partsTotal,
                laborTotal: acc.laborTotal + g.laborTotal,
                othersTotal: acc.othersTotal + g.othersTotal,
                grandTotal: acc.grandTotal + g.totalClaimAmount
            }),
            { totalClaims: 0, partsTotal: 0, laborTotal: 0, othersTotal: 0, grandTotal: 0 }
        );
    }, [groups]);

    const formatCurrency = (amount, currency = 'TRY') => {
        if (amount == null || isNaN(amount)) return '-';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency || 'TRY'
        }).format(amount);
    };

    const gtNumberTemplate = (row) => (
        <Tag value={row.gtNumber} severity="info" style={{ fontSize: '0.9rem', fontWeight: 'bold' }} />
    );

    const claimCountTemplate = (row) => (
        <Tag value={row.claimCount} severity="warning" rounded />
    );

    const footer = (
        <div className="flex justify-content-between align-items-center">
            <span className="text-sm text-gray-500">
                {th('invoiceApproval.provision.totalGroups') || 'Toplam Grup'}: {groups.length} | {' '}
                {th('invoiceApproval.provision.totalClaims') || 'Toplam Talep'}: {grandTotals.totalClaims}
            </span>
            <div className="flex gap-2">
                <Button
                    label={th('common.cancel') || 'İptal'}
                    icon="pi pi-times"
                    onClick={onHide}
                    className="p-button-outlined p-button-secondary"
                />
                <Button
                    label={th('invoiceApproval.provision.confirmAndProceed') || 'Onayla ve Devam Et'}
                    icon="pi pi-check"
                    onClick={onConfirm}
                    className="p-button-success"
                    disabled={groups.length === 0 || isLoading}
                />
            </div>
        </div>
    );

    return (
        <Dialog
            header={
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-eye text-xl text-blue-500" />
                    <span>{th('invoiceApproval.provision.title') || 'Provizyon Önizleme'}</span>
                </div>
            }
            visible={visible}
            style={{ width: '950px' }}
            breakpoints={{ '960px': '80vw', '641px': '95vw' }}
            modal
            footer={footer}
            onHide={onHide}
        >
            {/* Loading */}
            {isLoading && (
                <div className="flex flex-column align-items-center justify-content-center p-5">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                    <p className="mt-3 text-gray-500">
                        {th('invoiceApproval.provision.loading') || 'Provizyon hesaplanıyor...'}
                    </p>
                </div>
            )}

            {/* Error */}
            {!isLoading && isError && (
                <div className="flex flex-column align-items-center justify-content-center p-5 text-red-500">
                    <i className="pi pi-exclamation-triangle text-4xl mb-2" />
                    <p>{th('invoiceApproval.provision.error') || 'Provizyon yüklenirken hata oluştu.'}</p>
                </div>
            )}

            {/* Content */}
            {!isLoading && !isError && (
                <>
                    {/* Info banner */}
                    <div className="flex align-items-center gap-2 p-3 bg-blue-50 border-round border-1 border-blue-200 mb-3">
                        <i className="pi pi-info-circle text-blue-500 text-xl" />
                        <span className="text-sm">
                            {th('invoiceApproval.provision.infoMessage') ||
                                'Aşağıda onaylanacak faturaların GT numarası bazında gruplaması gösterilmektedir. Gruplamayı inceledikten sonra onay işlemine devam edebilirsiniz.'}
                        </span>
                    </div>

                    {isApproveAll && (
                        <div className="flex align-items-center gap-2 p-3 bg-yellow-50 border-round border-1 border-yellow-200 mb-3">
                            <i className="pi pi-exclamation-triangle text-yellow-500 text-xl" />
                            <span className="text-sm font-medium">
                                {th('invoiceApproval.provision.approveAllWarning') ||
                                    'Tümünü onaylama modu aktif. Filtrelere uyan tüm kayıtlar onaylanacaktır.'}
                            </span>
                        </div>
                    )}

                    {/* Gruplama tablosu */}
                    <DataTable
                        value={groups}
                        className="p-datatable-sm"
                        showGridlines
                        stripedRows
                        emptyMessage={th('invoiceApproval.provision.noData') || 'Önizlenecek veri bulunamadı.'}
                        dataKey="gtNumber"
                        rowHover
                    >
                        <Column
                            field="gtNumber"
                            header={th('invoiceApproval.provision.gtNumber') || 'GT Numarası'}
                            body={gtNumberTemplate}
                            style={{ minWidth: '120px', textAlign: 'center' }}
                        />
                        <Column
                            field="warrType"
                            header={th('invoiceApproval.table.warrantyType') || 'Garanti Tipi'}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            field="dealerName"
                            header={th('invoiceApproval.table.dealerName') || 'Bayi Adı'}
                            sortable
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="claimCount"
                            header={th('invoiceApproval.provision.claimCount') || 'Talep Sayısı'}
                            body={claimCountTemplate}
                            style={{ minWidth: '100px', textAlign: 'center' }}
                        />
                        <Column
                            field="partsTotal"
                            header={th('invoiceApproval.table.partsTotal') || 'Parça Toplamı'}
                            body={(r) => formatCurrency(r.partsTotal, r.currency)}
                            style={{ minWidth: '140px', textAlign: 'right' }}
                        />
                        <Column
                            field="laborTotal"
                            header={th('invoiceApproval.table.laborTotal') || 'İşçilik Toplamı'}
                            body={(r) => formatCurrency(r.laborTotal, r.currency)}
                            style={{ minWidth: '140px', textAlign: 'right' }}
                        />
                        <Column
                            field="othersTotal"
                            header={th('invoiceApproval.table.othersTotal') || 'Diğer Toplamı'}
                            body={(r) => formatCurrency(r.othersTotal, r.currency)}
                            style={{ minWidth: '140px', textAlign: 'right' }}
                        />
                        <Column
                            field="totalClaimAmount"
                            header={th('invoiceApproval.table.totalClaimAmount') || 'Toplam Tutar'}
                            body={(r) => (
                                <span className="font-bold text-blue-600">
                                    {formatCurrency(r.totalClaimAmount, r.currency)}
                                </span>
                            )}
                            style={{ minWidth: '160px', textAlign: 'right' }}
                        />
                    </DataTable>

                    <Divider className="my-3" />

                    {/* Genel toplam kartları */}
                    <div className="flex flex-wrap gap-3">
                        <div className="flex-1 p-3 bg-gray-50 border-round text-center" style={{ minWidth: '140px' }}>
                            <div className="text-sm text-gray-500 mb-1">
                                {th('invoiceApproval.table.partsTotal') || 'Parça Toplamı'}
                            </div>
                            <div className="font-semibold">{formatCurrency(grandTotals.partsTotal)}</div>
                        </div>
                        <div className="flex-1 p-3 bg-gray-50 border-round text-center" style={{ minWidth: '140px' }}>
                            <div className="text-sm text-gray-500 mb-1">
                                {th('invoiceApproval.table.laborTotal') || 'İşçilik Toplamı'}
                            </div>
                            <div className="font-semibold">{formatCurrency(grandTotals.laborTotal)}</div>
                        </div>
                        <div className="flex-1 p-3 bg-gray-50 border-round text-center" style={{ minWidth: '140px' }}>
                            <div className="text-sm text-gray-500 mb-1">
                                {th('invoiceApproval.table.othersTotal') || 'Diğer Toplamı'}
                            </div>
                            <div className="font-semibold">{formatCurrency(grandTotals.othersTotal)}</div>
                        </div>
                        <div className="flex-1 p-3 bg-blue-50 border-round border-1 border-blue-200 text-center" style={{ minWidth: '140px' }}>
                            <div className="text-sm text-blue-600 mb-1">
                                {th('invoiceApproval.provision.grandTotal') || 'Genel Toplam'}
                            </div>
                            <div className="font-bold text-lg text-blue-700">
                                {formatCurrency(grandTotals.grandTotal)}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Dialog>
    );
};

export default ProvisionPreviewDialog;
